import { useRef } from "react";
import cx from "classnames";

import {
  addTranslationData,
  useTranslation,
} from "@mywheels/common/lib/translation";

import { ModalPortal, useClickAway } from "web/ui";
import { useKeyDown } from "web/ui/useKeyDown";
import { useScrollLock } from "web/ui/useScrollLock";

import styles from "./MovePageTermsPaneModal.module.css";
import { CloseIcon } from "web/ui/icons/CloseIcon";

type Props = {
  onDismiss: () => void;
};

addTranslationData({
  en: {
    Actievoorwaarden: "Competition Terms and Conditions",
  },
});

export function MovePageTermsPaneModal({ onDismiss }: Props) {
  const { t, language } = useTranslation();

  const modalRef = useRef<HTMLDivElement>(null);

  useKeyDown("Escape", onDismiss);
  useClickAway(modalRef, onDismiss);

  useScrollLock();

  return (
    <ModalPortal>
      <div
        onClick={(e) => void e.stopPropagation()}
        className="fixed z-[2000] inset-0"
      >
        <div className="absolute inset-0 bg-black opacity-10" />

        <div
          ref={modalRef}
          className={cx(styles.modal, "bg-lightbluegray overflow-y-scroll")}
        >
          <button
            onClick={onDismiss}
            className="cursor-pointer h-[64px] w-[64px] flex flex-col justify-center items-center absolute top-0 right-0 bottom-0 text-gray600 hover:text-black"
          >
            <CloseIcon size={28} />
            <div className="hidden md:block text-xs text-muted leading-3">
              Esc
            </div>
          </button>

          <div className="p-4 md:p-8 lg:py-13 mx-auto max-w-[600px] w-full text-gray700">
            <div className="mb-6 md:mb-10 text-black font-display text-3xl lg:text-4xl">
              {t("Actievoorwaarden")}
            </div>
            {language === "en" ? (
              <div className="prose">
                <p>
                  <strong>Prizes:</strong> competition prizes for ‘The
                  Move(ment)’ include: One year's free use of MyWheels worth a
                  total of €6,000 to a maximum of €500 per month (3x). The
                  unused credit of a particular month will expire at the end of
                  that month. A journey balance in the amount of €35 (45x). A
                  well-stuffed MyWheels goody bag worth €50 (15x).
                  <br />
                  We will be dividing the entries into three categories and
                  select winners from each of the categories:
                </p>
                <ul>
                  <li>#TheMostFunnyMove: the funniest entry</li>
                  <li>#TheBestCarMove: the most beautiful dance with a car</li>
                  <li>#TheMostOriginalMove: the most original dance</li>
                </ul>

                <p>
                  <strong>How:</strong> MyWheels' competitions/promotions are
                  free to enter. You enter the competition by following MyWheels
                  on Instagram (
                  <a
                    href="https://www.instagram.com/mywheelsnl/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    @mywheelsnl
                  </a>
                  ) and sharing a video in a post or Story – all posts will be
                  included automatically. Posts and Stories that have been
                  posted as highlights will have twice as much chance of winning
                  than posts and Stories that have not. To do this, you will
                  have to use the hashtag #deelmee and tag
                  <a
                    href="https://www.instagram.com/mywheelsnl/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    @mywheelsnl
                  </a>{" "}
                  in your post.
                </p>

                <p>
                  <strong>Promoter:</strong> competitions such as ‘The
                  Move(ment)’ are operated by MyWheels acting under the trading
                  name Wheels4all B.V. with its registered offices in Amsterdam.
                  These Competition Terms and Conditions apply to all
                  competitions/promotions.
                </p>

                <p>
                  <strong>Use of entries:</strong> by entering this competition,
                  you automatically give your consent to MyWheels, acting under
                  the trading name Wheels4ll B.V., to share and use this content
                  on, for example, its own channels, website and/or to share it
                  on other media for a period of one (1) year. Under no
                  circumstances will we disclose personal data to a third party.
                </p>

                <p>
                  <strong>Who:</strong> for this competition, each entrant can
                  only participate once in each category. This competition is
                  open to Dutch residents aged 18 years or older. All other
                  entrants are excluded. Furthermore, winners must comply with
                  the{" "}
                  <a
                    href="https://mywheels.nl/algemene-voorwaarden"
                    target="_blank"
                    rel="noreferrer"
                  >
                    MyWheels General Terms and Conditions
                  </a>{" "}
                  for them to collect the prize.
                </p>

                <p>
                  <strong>Extra chance:</strong> tagging people in your
                  Story/post means that you will increase your chances of
                  standing out. We do not in any way encourage entrants to tag
                  people that have nothing to do with the Story/post.{" "}
                </p>

                <p>
                  <strong>Entrants:</strong> entrants shall comply with the
                  <a
                    href="https://www.reclamecode.nl/socialuitleg/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Advertising Code for Social Media &amp; Influencer Marketing
                  </a>
                  .
                </p>

                <p>
                  <strong>Term:</strong> the competition will end on Thursday,
                  30 September 2021. Winners will be contacted personally via
                  Instagram before Friday, 8 October 2021.
                </p>

                <p>
                  <strong>Panel:</strong> MyWheels and any party otherwise
                  connected to the competition will impartially determine the
                  winner. The panel, MyWheels’ employees and other parties
                  connected to the competition are excluded from the
                  competition.
                </p>

                <p>
                  <strong>Amendments:</strong> MyWheels may, at its own
                  discretion and without prior notice, change or modify these
                  Competition Terms and Conditions during the term, or change or
                  modify the competition/promotion without giving reasons.
                </p>

                <p>
                  <strong>Winner notification:</strong> winners will be informed
                  personally. By entering the competition, you agree to the
                  public disclosure of your name, entry, and Instagram account
                  on our own channels should you win one of the prizes.
                </p>

                <p>
                  <strong>Prize:</strong> prizes are not transferable to another
                  individual and no cash or other alternatives will be offered.
                </p>

                <p>
                  <strong>Personal data:</strong> personal data relating to the
                  competition/promotion will be used solely by Wheels4All B.V.
                  and will not be disclosed to a third party. Personal data will
                  be promptly deleted when the competition/promotion has ended.
                </p>

                <p>
                  <strong>Applicable law:</strong> these Competition Terms and
                  Conditions are governed by Dutch law.
                </p>

                <p>
                  <strong>Instagram:</strong> this competition and other
                  promotions are offered to you by MyWheels, acting under the
                  trading name Wheels4All B.V. This means that Instagram is used
                  as a platform and is, therefore, in no way a part of this
                  competition/promotion. We are thus complying with the
                  applicable{" "}
                  <a
                    href="https://help.instagram.com/179379842258600"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Promotion Guidelines of the platform
                  </a>
                  .
                </p>
              </div>
            ) : (
              <div className="prose">
                <p>
                  <strong>Prijs:</strong> met de winactie “The Move(ment)” wordt
                  drie keer één jaar gratis gebruik van MyWheels weggegeven
                  t.w.v. € 6.000,- met een maximaal bedrag van € 500,- per
                  maand. Tegoed dat niet wordt gebruikt komt per maand te
                  vervallen. Daarnaast maak je kans op rijtegoed t.w.v. € 35,-
                  (45x) en een MyWheels goed gevulde goodiebag t.w.v. € 50,-
                  (15x).
                  <br />
                  De deelnemers en winnaars worden door ons verdeeld in drie
                  categorieën:
                  <ul>
                    <li>#TheMostFunnyMove: de grappigste inzending;</li>
                    <li>#TheBestCarMove: de mooiste dans met een auto;</li>
                    <li>#TheMostOriginalMove: de meest originele dans;</li>
                  </ul>
                </p>

                <p>
                  <strong>Hoe:</strong> deelname aan een winactie van MyWheels
                  is gratis. Deelname vindt plaats wanneer je Mywheels volgt op
                  Instagram (
                  <a
                    href="https://www.instagram.com/mywheelsnl/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    @mywheelsnl
                  </a>
                  ) en een video deelt in een post of story, alle posts worden
                  automatisch meegenomen. Een post en story die is opgeslagen in
                  een highlight maken twee keer zoveel kans dan een story die
                  niet in een highlight is opgeslagen. Hierbij dien je de
                  hashtag #deelmee toe te voegen en{" "}
                  <a
                    href="https://www.instagram.com/mywheelsnl/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    @mywheelsnl
                  </a>{" "}
                  te taggen.
                </p>

                <p>
                  <strong>Verschaffer:</strong> winacties zoals “The Move(ment)”
                  worden uitgeschreven door MyWheels onder de handelsnaam
                  Wheels4all B.V. gevestigd te Amsterdam. Deze actievoorwaarden
                  zijn op winacties van toepassing.
                </p>

                <p>
                  <strong>Gebruik inzendingen:</strong> door deel te nemen aan
                  deze actie wordt automatisch akkoord gegeven aan MyWheels
                  onder de handelsnaam Wheels4ll B.V. voor het delen en
                  gebruiken van dit beeldmateriaal op bijvoorbeeld eigen
                  kanalen, website en/of voor het delen in overige media
                  gedurende een periode van één jaar. We zullen in geen geval
                  persoonsgegevens verstrekken aan derden.
                </p>

                <p>
                  <strong>Wie:</strong> iedere deelnemer kan maximaal één keer
                  per categorie deelnemen aan deze winactie. De minimum leeftijd
                  is 18 jaar en deelnemers dienen woonachtig te zijn in
                  Nederland. Alle andere deelnemers zijn uitgesloten. De winnaar
                  moet daarnaast voldoen aan de{" "}
                  <a
                    href="https://mywheels.nl/algemene-voorwaarden"
                    target="_blank"
                    rel="noreferrer"
                  >
                    algemene voorwaarden van MyWheels
                  </a>{" "}
                  om de prijs te kunnen innen.
                </p>

                <p>
                  <strong>Extra kans:</strong> door mensen te taggen die ook op
                  de story/post staan vergroot je jouw kans om op te vallen. Wij
                  stimuleren op geen enkele manier om mensen te taggen die niet
                  bij de story/post zijn betrokken.
                </p>

                <p>
                  <strong>Deelnemers:</strong> deelnemers dienen de{" "}
                  <a
                    href="https://www.reclamecode.nl/socialuitleg/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Reclame Social Media Code
                  </a>{" "}
                  na te leven.
                </p>

                <p>
                  <strong>Looptijd:</strong> de actie geldt tot en met donderdag
                  30 september 2021. De winnaars zullen persoonlijk worden
                  benaderd via Instagram vóór vrijdag 8 oktober 2021.
                </p>

                <p>
                  <strong>Jury:</strong> het bepalen van de winnaar gebeurt
                  onpartijdig door MyWheels en eventuele betrokken partijen. De
                  jury, MyWheels werknemers en betrokken partijen zijn ook
                  uitgesloten van het winnen van prijzen.
                </p>

                <p>
                  <strong>Wijzigingen:</strong> MyWheels kan, naar eigen inzicht
                  en zonder voorafgaande kennisgeving, deze actievoorwaarden
                  gedurende de looptijd wijzigen of aanpassen, dan wel zonder
                  opgave van reden de actie wijzigen of aanpassen.
                </p>

                <p>
                  <strong>Bekendmaking:</strong> de winnaars worden persoonlijk
                  geïnformeerd. Door deelname ga je akkoord met het publiekelijk
                  bekendmaken van je naam, inzending en Instagram-account op
                  onze eigen kanalen indien je één van de prijzen wint.
                </p>

                <p>
                  <strong>De prijs:</strong> de prijs is niet inwisselbaar voor
                  geld of andere goederen noch overdraagbaar naar een ander
                  individu.
                </p>

                <p>
                  <strong>Persoonsgegevens:</strong> persoonsgegevens die in het
                  kader van winacties worden verkregen, worden uitsluitend
                  gebruikt door Wheels4All B.V. voor de winactie en worden niet
                  verstrekt aan derden. Persoonsgegevens worden na afloop
                  vernietigd.
                </p>

                <p>
                  <strong>Recht:</strong> op deze voorwaarden is het Nederlands
                  recht van toepassing.
                </p>

                <p>
                  <strong>Instagram:</strong> deze en verdere promoties worden
                  aangeboden door MyWheels onder de handelsnaam Wheels4All B.V..
                  Dit wil zeggen dat Instagram als een platform wordt gebruikt
                  maar in geen enkel opzicht een onderdeel is van deze promotie.
                  Hiermee voldoen wij aan de{" "}
                  <a
                    href="https://help.instagram.com/179379842258600"
                    target="_blank"
                    rel="noreferrer"
                  >
                    promotievoorwaarden van het platform
                  </a>
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
